import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { SalaryDistributionUserFormInterface } from '@zaoblako/shared/modules/document/dtos/salary-distribution/form/salary-distribution-user-form.dto';
import {
  SaleDistributionFormDto,
  type SaleDistributionFormInterface
} from '@zaoblako/shared/modules/document/dtos/salary-distribution/form/sale-distribution-form.dto';
import type { SalaryDistributionStatInterface } from '@zaoblako/shared/modules/document/dtos/salary-distribution/salary-distribution-stat.dto';
import { HelpdeskTaskReportMethodEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.report.method.enum';
import type { ProjectTimeReportItemsQueryInterface } from '@zaoblako/shared/modules/project/dto/project-time-report/project-time-report-items-query.dto';
import { ProjectTimeReportSubtotalByEnum } from '@zaoblako/shared/modules/project/enum/project-time-report-subtotal-by.enum';
import { PeriodChunkUnitEnum } from '@zaoblako/shared/modules/dates/enums/period-chunk-unit.enum';
import type { UserFindQueryInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find-query.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { SalaryDistributionService } from '@zaoblako/web/core/services/finance/salary-distribution.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { QueryParamsService } from '@zaoblako/web/core/services/query-params.service';
import { UserService } from '@zaoblako/web/core/services/security/user.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { UserKindEnum } from '@zaoblako/shared/modules/security/enums/user.kind.enum';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { DaterangepickerComponent } from '@zaoblako/web/shared/dumbs/daterangepicker/daterangepicker.component';
import { CompanySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/company-single-select/company-single-select.component';
import { CurrencySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/currency-single-select/currency-single-select.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { DocumentTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/document-type-single-select/document-type-single-select.component';
import { OperationTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/operation-type-single-select/operation-type-single-select.component';
import { UserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-single-select/user-single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { RptRegistryTableChromeDirective } from '@zaoblako/web/shared/directives/rpt-registry-table-chrome.directive';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { JoinWithChildPipe } from '@zaoblako/web/shared/pipes/join-with-child.pipe';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { flatten } from 'flat';
import { set } from 'lodash';
import moment from 'moment-timezone';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { TableModule } from 'primeng/table';
import { isNil } from 'lodash';
import { forkJoin, Observable, of, Subscription, switchMap, tap } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-document-salary-distribution-form',
  templateUrl: './document-salary-distribution-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    DaterangepickerComponent,
    JoinPipe,
    ButtonComponent,
    TableModule,
    TableComponent,
    UserSingleSelectComponent,
    DirectionSingleSelectComponent,
    DatepickerComponent,
    InputNumberModule,
    FormsModule,
    CheckboxModule,
    SpinnerComponent,
    JoinWithChildPipe,
    DialogModule,
    UnitSingleSelectComponent,
    OperationTypeSingleSelectComponent,
    DocumentTypeSingleSelectComponent,
    CurrencySingleSelectComponent,
    CompanySingleSelectComponent,
    RptRegistryTableChromeDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentSalaryDistributionFormComponent
  extends BaseFormComponent<SaleDistributionFormInterface>
  implements OnInit, OnDestroy {
  @Input() public readonly index: number;

  public distribution: SaleDistributionFormInterface = {
    from: moment().subtract(1, 'month').toDate(),
    to: moment().toDate(),
    settings: {
      direction: {
        target: null,
        distribute: false
      },
      unit: {
        target: null,
        distribute: false
      }
    },
    date: moment().toDate(),
    company: null,
    documentType: null,
    salaryDistributionUsers: []
  };

  public dto = SaleDistributionFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public directions: DirectionFindInterface[] = [];
  public currencies: CurrencyFindInterface[] = [];

  public sort = 'user.name:string';
  public isShowUserSelect = false;

  public userColumns: CrmTableColumn[] = [
    { label: 'Сотрудник', name: 'user', sort: 'user.name:string' },
    { label: 'Оклад', class: 'w-10rem' },
    { class: 'w-3rem' }
  ];

  public isShowUserWarning = false;
  public usersWithoutCounterparty: UserFindInterface[] = [];

  public isShowWarningDialog = false;
  public stat: SalaryDistributionStatInterface;

  private querySubscription$: Subscription;

  public constructor(
    private readonly formStoreService: FormStoreService,
    public readonly globalService: GlobalService,
    private readonly salaryDistributionService: SalaryDistributionService,
    private readonly directionService: DirectionService,
    private readonly userService: UserService,
    private readonly errorService: ErrorService,
    private readonly queryParamsService: QueryParamsService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.directionService.find<DirectionFindInterface>().subscribe((directions) => {
      this.directions = directions;
      this.cdRef.markForCheck();
    });
  }

  public ngOnDestroy(): void {
    this.querySubscription$?.unsubscribe();
  }

  public saveDistribution() {
    const requiredAnalyticalFeatures = this.globalService.globalInfo?.requiredAnalyticalFeatures;
    if (
      (requiredAnalyticalFeatures?.directionsRequired &&
        !(this.distribution.settings.direction?.distribute || this.distribution.settings.direction?.target)) ||
      (requiredAnalyticalFeatures?.unitsRequired &&
        !(this.distribution.settings.unit?.distribute || this.distribution.settings.unit?.target))
    ) {
      this.errors = Object.create({});
      this.errors['settings'] = Object.create({});
      if (
        requiredAnalyticalFeatures?.directionsRequired &&
        !(this.distribution.settings.direction?.distribute || this.distribution.settings.direction?.target)
      ) {
        this.errors.settings.direction = 'Выберите или распределите бюджетную статью' as any;
      }
      if (
        requiredAnalyticalFeatures?.unitsRequired &&
        !(this.distribution.settings.unit?.distribute || this.distribution.settings.unit?.target)
      ) {
        this.errors.settings.unit = 'Выберите или распределите бизнес единицу' as any;
      }
      this.cdRef.markForCheck();
      return;
    }

    const validation = this.checkValidate(this.distribution);
    this.normalizeSalaryDistributionUsersErrors();
    if (!validation.valid) {
      this.errorService.addCustomError('Ошибка валидации', this.formatValidationErrorsForToast());
      this.cdRef.markForCheck();
      return;
    }

    this.formStoreService.setLoading(true, this.index);

    const userIds = [
      ...new Set(
        this.distribution.salaryDistributionUsers
          .map((distributionUser) => distributionUser.user?.id)
          .filter(Boolean)
      )
    ];

    const users$ =
      userIds.length > 0
        ? forkJoin(userIds.map((id) => this.userService.findById<UserFindInterface>(id)))
        : of([] as UserFindInterface[]);

    users$
        .pipe(
          tap((users) => {
            this.distribution.salaryDistributionUsers = this.distribution.salaryDistributionUsers.map(
              (distributionUser) => ({
                ...distributionUser,
                user: users.find((user) => user?.id === distributionUser.user?.id) ?? distributionUser.user
              })
            );
          }),
          switchMap(() => {
            this.usersWithoutCounterparty = this.distribution.salaryDistributionUsers
              .map(({ user }) => user)
              .filter((user) => !user.contragent);

            if (this.usersWithoutCounterparty?.length) {
              return of(null);
            }
            return this.salaryDistributionService.getStat(this.distribution);
          })
        )
        .subscribe({
          next: (stat) => {
            this.stat = stat;

            if (!stat) {
              this.isShowUserWarning = true;
              this.cdRef.markForCheck();
            } else if (stat?.total?.total) {
              this.isShowWarningDialog = true;
              this.cdRef.markForCheck();
            } else {
              this.createDistribution();
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
  }

  public addUser(user: UserFindInterface) {
    if (!Array.isArray(this.distribution.salaryDistributionUsers)) {
      this.distribution.salaryDistributionUsers = [];
    }
    this.distribution.salaryDistributionUsers.push({
      user,
      amount: 0
    });
    this.distribution.salaryDistributionUsers = [...this.distribution.salaryDistributionUsers];
    this.isShowUserSelect = false;
    this.cdRef.markForCheck();
  }

  public setDistributionPeriod(dates: [Date, Date]) {
    this.distribution.from = dates[0];
    this.distribution.to = dates[1];
  }

  public toggleAddUser() {
    this.isShowUserSelect = true;
    this.cdRef.markForCheck();
  }

  public sortChange(sort: string) {
    this.sort = sort;
  }

  public toItem(item: any): SalaryDistributionUserFormInterface {
    return item as SalaryDistributionUserFormInterface;
  }

  public deleteUser(item: SalaryDistributionUserFormInterface) {
    this.distribution.salaryDistributionUsers = this.distribution.salaryDistributionUsers.filter(
      (_item) => _item.user?.id !== item.user?.id
    );
  }

  public changeAmount(item: SalaryDistributionUserFormInterface, amount: number) {
    if (isNaN(amount) || !amount) {
      setTimeout(() => {
        item.amount = 0;
        this.cdRef.markForCheck();
      }, 10);
    }
    this.distribution.salaryDistributionUsers = [...this.distribution.salaryDistributionUsers];
  }

  public createDistribution() {
    this.salaryDistributionService.create<SaleDistributionFormInterface>(this.distribution).subscribe({
      next: (distribution) => {
        this.formStoreService.setLoading(false, this.index);
        this.errorService.addSuccessMessage('Распределение ЗП создано');
        this.formStoreService.saveNext(distribution, this.index);
      },
      error: () => {
        this.formStoreService.setLoading(false, this.index);
      }
    });
  }

  public redirectToTimeReport() {
    const queryParams = {
      from: moment(this.distribution.from).format('YYYY-MM-DD') as unknown as Date,
      to: moment(this.distribution.to).format('YYYY-MM-DD') as unknown as Date,
      method: HelpdeskTaskReportMethodEnum.BY_TIME,
      subtotalBy: ProjectTimeReportSubtotalByEnum.PROJECT,
      plotPeriod: PeriodChunkUnitEnum.WEEK
    } as ProjectTimeReportItemsQueryInterface & {
      subtotalBy?: ProjectTimeReportSubtotalByEnum;
      plotPeriod?: PeriodChunkUnitEnum;
    };
    if (this.distribution.settings?.direction?.distribute && this.stat.total?.withoutDirections) {
      queryParams.withoutDirection = true;
    }
    if (this.distribution.settings?.unit?.distribute && this.stat.total?.withoutUnits) {
      queryParams.withoutUnit = true;
    }
    if (this.distribution.salaryDistributionUsers?.length) {
      queryParams.userIds = this.distribution.salaryDistributionUsers?.map((user) => user.user?.id);
    }

    this.querySubscription$ = this.queryParamsService
      .getUrlTreeWithDigest(['/project/time-report'], queryParams, true)
      .subscribe({
        next: (urlTree) => {
          const url = String(urlTree);
          window.open(url, '_blank');
          this.isShowWarningDialog = false;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        },
        error: (err) => {
          console.error('Failed to generate URL with digest:', err);
          this.errorService.addCustomError('Ошибка', 'Произошла ошибка, вернитесь на главную и попробуйте снова.');
        }
      });
  }

  public get excludedUserIds(): number[] {
    return (this.distribution.salaryDistributionUsers ?? [])
      .map((item) => item.user?.id)
      .filter(Boolean);
  }

  public redirectToUserList() {
    const queryParams: UserFindQueryInterface = { kind: UserKindEnum.EMPLOYEE };

    this.querySubscription$ = this.queryParamsService
      .getUrlTreeWithDigest(['/user/list'], queryParams, true)
      .subscribe({
        next: (urlTree) => {
          const url = String(urlTree);
          window.open(url, '_blank');
          this.closeDialog();
        },
        error: (err) => {
          console.error('Failed to generate URL with digest:', err);
          this.errorService.addCustomError('Ошибка', 'Произошла ошибка, вернитесь на главную и попробуйте снова.');
        }
      });
  }

  public closeDialog() {
    this.isShowUserWarning = false;
    this.formStoreService.setLoading(false, this.index);
    this.cdRef.markForCheck();
  }

  public changeCompany() {
    this.currencies = [];
    this.distribution.currency = null;

    if (this.distribution.company?.currencies?.length) {
      this.currencies = this.distribution.company.currencies;
      setTimeout(() => {
        this.distribution.currency = this.currencies.find((currency) => currency.isDefault);
        this.cdRef.markForCheck();
      });
    }
  }

  private normalizeSalaryDistributionUsersErrors(): void {
    if (!this.errors) {
      return;
    }
    const errors = this.errors as Record<string, unknown>;
    const salaryDistributionUsers: unknown[] = Array.isArray(errors.salaryDistributionUsers)
      ? [...errors.salaryDistributionUsers]
      : typeof errors.salaryDistributionUsers === 'string'
        ? [errors.salaryDistributionUsers]
        : [];

    for (const key of Object.keys(errors)) {
      const match = /^salaryDistributionUsers\[(\d+)](?:\.(.+))?$/.exec(key);
      if (!match) {
        continue;
      }
      const index = Number(match[1]);
      const path = match[2];
      const value = errors[key];
      if (path) {
        if (!salaryDistributionUsers[index] || typeof salaryDistributionUsers[index] !== 'object') {
          salaryDistributionUsers[index] = {};
        }
        set(salaryDistributionUsers[index] as object, path, value);
      } else {
        salaryDistributionUsers[index] = value;
      }
      delete errors[key];
    }

    if (salaryDistributionUsers.length) {
      errors.salaryDistributionUsers = salaryDistributionUsers;
    } else if (typeof errors.salaryDistributionUsers === 'string') {
      errors.salaryDistributionUsers = [errors.salaryDistributionUsers];
    }
  }

  private flattenErrorMessages(value: unknown): Record<string, string> {
    if (isNil(value)) {
      return {};
    }
    if (typeof value === 'string') {
      return { '': value };
    }
    if (typeof value !== 'object') {
      return { '': String(value) };
    }
    return flatten(value) as Record<string, string>;
  }

  private formatValidationErrorsForToast(): string {
    if (!this.errors) {
      return '';
    }
    const errors = this.errors as Record<string, unknown>;
    const lines: string[] = [];

    for (const [key, value] of Object.entries(errors)) {
      if (key === 'salaryDistributionUsers' && Array.isArray(value)) {
        for (const [index, userError] of value.entries()) {
          if (isNil(userError)) {
            continue;
          }
          if (typeof userError === 'string') {
            lines.push(`[Сотрудник ${index + 1}]: ${userError}`);
            continue;
          }
          for (const [path, message] of Object.entries(this.flattenErrorMessages(userError))) {
            lines.push(`[Сотрудник ${index + 1}${path ? `, ${path}` : ''}]: ${message}`);
          }
        }
        continue;
      }
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        for (const [path, message] of Object.entries(this.flattenErrorMessages(value))) {
          lines.push(`[${key}${path ? `.${path}` : ''}]: ${message}`);
        }
        continue;
      }
      lines.push(`[${key}]: ${String(value)}`);
    }

    return lines.join('\n');
  }
}
